import { useQuery, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { removeSupabaseChannelByName, subscribePostgresChannel } from "@/lib/realtime-channel";
import { bindRealtimeVisibilityPause } from "@/lib/realtime-visibility-pause";
import { REALTIME_PAUSE_ON_HIDDEN_ENABLED } from "@/lib/workspace-conversation-realtime-hub";
import { useToast } from "@/hooks/use-toast";
import { useEffect, useRef } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import type { DealWithStage, DealUpdate } from "@/types/deal";

function hasActiveDealsDashboardStats(queryClient: QueryClient, workspaceId: string): boolean {
  return (
    queryClient.getQueryCache().findAll({
      queryKey: ["deals-dashboard-stats", workspaceId],
      type: "active",
    }).length > 0
  );
}

export function useDeals(workspaceId: string | null) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const queryKey = ["deals", workspaceId];
  const reconcileTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const invalidateDealsData = () => {
    queryClient.invalidateQueries({ queryKey });
  };

  const invalidateDealsStatsIfActive = () => {
    if (workspaceId && hasActiveDealsDashboardStats(queryClient, workspaceId)) {
      queryClient.invalidateQueries({ queryKey: ["deals-dashboard-stats", workspaceId] });
    }
  };

  const invalidateDeals = () => {
    invalidateDealsData();
    invalidateDealsStatsIfActive();
    queryClient.invalidateQueries({ queryKey: ["deals-funnel-leads-without-deal"] });
  };

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!workspaceId) return [];
      const { data, error } = await supabase
        .from("deals")
        .select("*, deal_stages(*), leads(id, name, phone, email, funnel_id)")
        .eq("workspace_id", workspaceId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as unknown as DealWithStage[];
    },
    enabled: !!workspaceId,
  });

  // Realtime subscription for deals
  useEffect(() => {
    if (!workspaceId) return;

    const channelName = `deals-realtime-${workspaceId}`;
    let channel: RealtimeChannel | null = null;

    const onRealtimeChange = () => {
      invalidateDealsData();
      invalidateDealsStatsIfActive();
    };

    const subscribeChannel = () => {
      if (REALTIME_PAUSE_ON_HIDDEN_ENABLED && typeof document !== "undefined" && document.hidden) {
        return;
      }
      if (channel) return;
      channel = subscribePostgresChannel(
        channelName,
        { event: "*", schema: "public", table: "deals", filter: `workspace_id=eq.${workspaceId}` },
        onRealtimeChange,
      );
    };

    const teardownChannel = () => {
      if (!channel) return;
      removeSupabaseChannelByName(channelName);
      supabase.removeChannel(channel);
      channel = null;
    };

    const reconcileDeals = () => {
      if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
      reconcileTimerRef.current = setTimeout(() => {
        invalidateDeals();
      }, 300);
    };

    const unbind = bindRealtimeVisibilityPause({
      pauseEnabled: REALTIME_PAUSE_ON_HIDDEN_ENABLED,
      subscribe: subscribeChannel,
      teardown: teardownChannel,
      onReconcile: reconcileDeals,
    });

    return () => {
      unbind();
      if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
    };
  }, [workspaceId, queryClient]);

  const createDealFromLead = useMutation({
    mutationFn: async (params: { workspaceId: string; leadId: string; title?: string; memberId?: string }) => {
      const { data, error } = await supabase.rpc("create_deal_from_lead", {
        p_workspace_id: params.workspaceId,
        p_lead_id: params.leadId,
        p_title: params.title || null,
        p_member_id: params.memberId || null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      invalidateDeals();
      toast({ title: "Negociação criada", description: "A negociação foi criada com sucesso." });
    },
    onError: (e: any) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const moveDealStage = useMutation({
    mutationFn: async (params: { dealId: string; toStageId: string; memberId?: string; reason?: string }) => {
      const { error } = await supabase.rpc("move_deal_stage", {
        p_deal_id: params.dealId,
        p_to_stage_id: params.toStageId,
        p_member_id: params.memberId || null,
        p_reason: params.reason || null,
      });
      if (error) throw error;
    },
    onSuccess: () => invalidateDeals(),
    onError: (e: any) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const updateDeal = useMutation({
    mutationFn: async ({ id, ...updates }: DealUpdate & { id: string }) => {
      const { error } = await supabase.from("deals").update(updates).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => invalidateDeals(),
    onError: (e: any) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const deleteDeal = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("deals").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateDeals();
      toast({ title: "Negociação excluída" });
    },
    onError: (e: any) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  return { ...query, deals: query.data ?? [], createDealFromLead, moveDealStage, updateDeal, deleteDeal };
}
